import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { PHOTOS_SEARCH } from 'constants/mixpanel';
import { useImageSearch } from 'hooks/useImageSearch';
import { useSideMenu } from 'hooks/useSideMenu';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { SearchInput } from 'modules/common/components/SearchInput/SearchInput';
import { ReactElement, memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NewWidget } from 'widgets/Widget.types';

import { InfiniteList } from './PhotoList';
import { PhotoMenuProvider } from './PhotoMenuContext';
import { IMAGE_PROVIDERS, RANDOM_TAG } from './PhotoWidgetMenu.config';
import { AuthorCreditProps } from './PhotoWidgetMenu.types';
import { SuggestedTagList } from './SuggestedTagList';

export interface PhotoWidgetMenuProps {
  isMenuActive: boolean;
  onItemClick: (widget: NewWidget | NewWidget[]) => void;
  disableDragDrop?: boolean;
}

export const PhotoWidgetMenu = memo(
  ({ isMenuActive, onItemClick, disableDragDrop }: PhotoWidgetMenuProps): ReactElement | null => {
    const { data, query, setQuery, nextPage, isLoadingMore, clearSearch } = useImageSearch();
    const [queryStr, setQueryStr] = useState('');
    const [{ author, provider }, setImageMeta] = useState<AuthorCreditProps>({ provider: IMAGE_PROVIDERS });
    const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.photosMenu' });
    const [defaultTag] = useState<string>(RANDOM_TAG);
    const hasSetDefaultTag = useRef<boolean>(false);

    const setDefaultSearch = () => {
      if (hasSetDefaultTag.current) return;
      onSearch(defaultTag, true);
      hasSetDefaultTag.current = true;
    };

    useSideMenu({ isMenuActive, onOpenMenu: setDefaultSearch });

    const onSearch = useCallback(
      (searchValue: string, isFirstSearch: boolean = false) => {
        setQuery(searchValue);
        setImageMeta((prev) => ({ ...prev, author: undefined, provider: IMAGE_PROVIDERS }));

        if (!isFirstSearch) {
          Mixpanel.track(PHOTOS_SEARCH.eventName, {
            query: searchValue,
          });
        }
      },
      [setQuery],
    );

    const onClearSearch = () => {
      clearSearch();
      // reset back to default tag previously loaded
      onSearch(defaultTag);
    };

    const onSuggestedTagClick = useCallback(
      (searchValue: string) => {
        setQueryStr(searchValue);
        onSearch(searchValue);
      },
      [onSearch],
    );

    return (
      <PhotoMenuProvider setImageMeta={setImageMeta} onItemClick={onItemClick}>
        <Box display={isMenuActive ? 'block' : 'none'} h='full'>
          <Flex direction='column' h='full'>
            <Box p='4' paddingBottom='2'>
              <SearchInput
                value={queryStr}
                setValue={setQueryStr}
                onSearch={onSearch}
                clearSearch={onClearSearch}
                label={t('searchPlaceholder' as keyof typeof t)}
                clearButtonLabel={t('clearButtonLabel' as keyof typeof t)}
              />
            </Box>
            <SuggestedTagList onTagClick={onSuggestedTagClick} />
            {query && !data.length && !isLoadingMore && <NoImageFound query={query} />}
            {query && !!data.length && (
              <InfiniteList
                data={data}
                numberOfItems={data.length}
                loadMore={nextPage}
                disableDragDrop={disableDragDrop}
              />
            )}
            {!!data.length && <AuthorCredit author={author} provider={provider} />}
            {query && isLoadingMore && (
              <Spinner data-testid='image-widgetmenu-spinner' alignSelf='center' color='brand.500' />
            )}
          </Flex>
        </Box>
      </PhotoMenuProvider>
    );
  },
);
PhotoWidgetMenu.displayName = 'PhotoWidgetMenu';

export const AuthorCredit = ({ author, provider }: AuthorCreditProps) => {
  return (
    <Text
      data-testid={'credit-hover'}
      display={'block'}
      position={'absolute'}
      bottom={0}
      left={0}
      fontSize={'xs'}
      fontWeight={'medium'}
      lineHeight={'base'}
      width={'full'}
      color={'white'}
      backgroundColor={'black-alpha.700'}
      paddingY={'0.325rem'}
      paddingLeft={'0.6rem'}
    >
      <AuthorCreditContent author={author} provider={provider} />
    </Text>
  );
};

const AuthorCreditContent = ({ author, provider }: AuthorCreditProps) => {
  const { t } = useTranslation('editor_widget_menu', {
    useSuspense: false,
    keyPrefix: 'sideMenu.photosMenu.authorCredit',
  });
  if (author) {
    return (
      <>
        {`${t('imageBy')} `}
        <b>{author}</b>
        {` ${t('on')} `}
        <b>{provider}</b>
      </>
    );
  }

  return (
    <>
      {`${t('imagesFrom')} `}
      <b>{provider}</b>
    </>
  );
};

interface NoImagesFoundProps {
  query: string;
}

export const NoImageFound = ({ query }: NoImagesFoundProps): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.photosMenu' });
  return <Text p='4'>{t('noImagesFound' as keyof typeof t, { query })}</Text>;
};
